import { useEffect, useState, useRef } from "react";

import { useDebounce } from "@/src/hooks/use-debounce";
import { logger } from "@/src/lib/logging/logger";
import type { CPT } from "@/src/models/cpt";

export const useCPTSearch = (searchTerm: string) => {
  const [results, setResults] = useState<CPT[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentControllerRef = useRef<AbortController | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 100);

  useEffect(() => {
    if (currentControllerRef.current) {
      currentControllerRef.current.abort();
    }

    setError(null);

    if (!debouncedSearchTerm.trim()) {
      setResults([]);
      setLoading(false);

      return;
    }

    setLoading(true);

    const controller = new AbortController();

    currentControllerRef.current = controller;
    const signal = controller.signal;

    const runSearch = async () => {
      try {
        if (signal.aborted) {
          return;
        }

        const response = await fetch(`/api/cpt/search?q=${encodeURIComponent(debouncedSearchTerm)}&limit=50`, {
          signal,
        });

        if (signal.aborted) {
          return;
        }

        if (!response.ok) {
          logger.error("CPT search failed", {
            status: response.status,
            statusText: response.statusText,
            query: debouncedSearchTerm,
          });
          throw new Error("Failed to search CPT codes");
        }

        const searchResults = await response.json();

        if (!signal.aborted) {
          setResults(searchResults as CPT[]);
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }

        logger.error("CPT search error", {
          error: err,
          query: debouncedSearchTerm,
        });

        const errorMessage = err instanceof Error ? err.message : "Failed to search CPT codes";

        setError(errorMessage);
        setResults([]);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    runSearch();

    return () => {
      if (currentControllerRef.current) {
        currentControllerRef.current.abort();
      }
    };
  }, [debouncedSearchTerm]);

  return { results, loading, error };
};
